import React, { FC, useMemo, useCallback } from "react";
import { ColumnConfig, numberSort, SortDirections, TextAlignment, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { JobPerformanceSlowestJobType } from "@app/operations/job-performance/jobPerformanceInterfaces";

type SlowestJobTypeRow = {
    [wafflesKeyColumnName]: string;
    avgQueueWait: string;
    avgRunTime: string;
    isFirst: boolean;
    jobTypeName: string;
    jobs: number;
    quarantined: number;
    quarantinedChip: React.ReactNode;
    queue: string;
    slowerThanAvg: number;
    slowerThanAvgFormatted: React.ReactNode;
    worstRunTime: string;
};

type Props = {
    data: JobPerformanceSlowestJobType[];
    loading: boolean;
};

function formatMultiplier(v: number): React.ReactNode {
    const cls = v >= 7 ? 'jp-multiplier--high' : v >= 3 ? 'jp-multiplier--mid' : 'jp-multiplier--low';
    return <span className={cls}>{v.toFixed(1)}x</span>;
}

export const JobPerformanceSlowestJobTypesTable: FC<Props> = ({ data, loading }) => {
    /* oxlint-disable sort-keys */
    const tableData = useMemo<SlowestJobTypeRow[]>(() =>
        data.map((r, index) => ({
            avgQueueWait: r.avgQueueWaitMinutes.toFixed(1),
            avgRunTime: r.avgRunTimeMinutes.toFixed(1),
            isFirst: index === 0,
            jobTypeName: r.jobTypeName,
            jobs: r.jobsProcessed,
            quarantined: r.quarantinedCount,
            quarantinedChip: r.quarantinedCount > 0
                ? <span className="jp-chip jp-chip--active">{r.quarantinedCount}</span>
                : <span className="jp-chip jp-chip--muted">0</span>,
            queue: r.jobQueueName,
            slowerThanAvg: r.slowerThanAvgMultiplier,
            slowerThanAvgFormatted: formatMultiplier(r.slowerThanAvgMultiplier),
            [wafflesKeyColumnName]: r.jobTypeId.toString(),
            worstRunTime: r.worstRunTimeMinutes.toFixed(1),
        })), [data]);
    /* oxlint-enable sort-keys */

    const handleSlowestSort = useCallback((segment: SlowestJobTypeRow[], column: keyof SlowestJobTypeRow, direction: SortDirections): SlowestJobTypeRow[] => {
        const mult = direction === SortDirections.Asc ? 1 : -1;
        if (column === 'avgQueueWait' || column === 'avgRunTime' || column === 'worstRunTime') {
            return numberSort(segment, column, mult);
        }
        if (column === 'slowerThanAvg' || column === 'slowerThanAvgFormatted') {
            return [...segment].sort((a, b) => (a.slowerThanAvg - b.slowerThanAvg) * mult);
        }
        if (column === 'quarantinedChip') {
            return [...segment].sort((a, b) => (a.quarantined - b.quarantined) * mult);
        }
        if (column === 'jobs' || column === 'quarantined') {
            return [...segment].sort((a, b) => ((a[column] as number) - (b[column] as number)) * mult);
        }
        return [...segment].sort((a, b) => (String(a[column]) > String(b[column]) ? 1 : -1) * mult);
    }, []);

    const rowDecorator = useCallback((row: SlowestJobTypeRow): string =>
        row.isFirst ? "info" : "", []);

    const columnConfig: ColumnConfig = useMemo(() => ({
        avgQueueWait: { alignment: TextAlignment.Right, displayText: 'Avg Queue Wait (Min)', sizeContentToFit: true, sortOrder: 50, testId: 'slowest-avg-queue-wait' },
        avgRunTime: { alignment: TextAlignment.Right, displayText: 'Avg Run Time (Min)', sizeContentToFit: true, sortOrder: 60, testId: 'slowest-avg-run-time' },
        isFirst: { displayText: 'Is First', hidden: true, testId: 'slowest-is-first' },
        jobs: { alignment: TextAlignment.Right, displayText: 'Jobs Processed', sizeContentToFit: true, sortOrder: 30, testId: 'slowest-jobs' },
        jobTypeName: { displayText: 'Job Type', sortOrder: 10, testId: 'slowest-job-type-name' },
        quarantined: { displayText: 'Quarantined', hidden: true, sizeContentToFit: true, testId: 'slowest-quarantined' },
        quarantinedChip: { alignment: TextAlignment.Right, displayText: 'Quarantined', sizeContentToFit: true, sortOrder: 70, testId: 'slowest-quarantined-chip' },
        queue: { displayText: 'Job Queue Name', sortOrder: 20, testId: 'slowest-queue' },
        slowerThanAvg: { displayText: 'Slower Than Avg (x)', hidden: true, sizeContentToFit: true, testId: 'slowest-multiplier' },
        slowerThanAvgFormatted: { alignment: TextAlignment.Right, displayText: 'Slower Than Avg (x)', sizeContentToFit: true, sortOrder: 80, testId: 'slowest-multiplier-formatted' },
        worstRunTime: { alignment: TextAlignment.Right, displayText: 'Worst Run Time (Min)', sizeContentToFit: true, sortOrder: 40, testId: 'slowest-worst-run-time' },
    }), []);

    return (
        <Waffles<SlowestJobTypeRow>
            columns={columnConfig}
            customSort={handleSlowestSort}
            data={tableData}
            rowDecorator={rowDecorator}
            loading={loading}
            testId="job-performance__slowest-job-types-table"
        />
    );
};
